import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../../i18n/bookingCopy';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HostTutorBookingProposeChangeDialog from '../../components/HostTutorBookingProposeChangeDialog';
import TutorBookingRequestCard from '../../components/tutor/TutorBookingRequestCard';
import TutorSeriesRequestCard from '../../components/tutor/TutorSeriesRequestCard';
import TutorBookingRequestsPageShell from '../../components/tutor/TutorBookingRequestsPageShell';
import TutorBookingFilterTabs from '../../components/tutor/TutorBookingFilterTabs';
import TutorBookingStudentSearchField from '../../components/tutor/TutorBookingStudentSearchField';
import BookingRejectionDialog from '../../components/BookingRejectionDialog';
import { hasPendingTutorChangeProposal } from '../../components/TutorBookingRequestTableCells';
import { tutorApiClient } from '../../tutorApiClient';
import { hostApiClient } from '../../hostApiClient';
import { authService } from '../../services/AuthService';
import { HostTutorBookingResponse, TutorBookingFilter, TutorBookingSummaryCountsResponse } from '../../models/Tutor';
import {
  canCancelConfirmedTutorBooking,
  canHostCancelTutorBookingChangeProposal,
  canHostProposeTutorBookingChange,
  isPastTutorBookingStart,
  isTutorBookingModeratable,
  tutorBookingAcceptDisabled,
  tutorBookingDeclineDisabled,
} from '../../utils/tutorBookingStatus';
import { canRefundTutorBooking } from '../../utils/tutorBookingPayment';
import { canCollectTutorBookingPayment } from '../../utils/bookingPaymentCollection';
import {
  extractTutorBookingsPageTotalFromLegacy,
  TUTOR_BOOKING_REQUEST_FILTERS,
} from '../../utils/tutorBookingFilters';
import { notifyTutorBookingNeedsActionCountChanged } from '../../utils/tutorBookingNeedsActionCount';
import { httpStatusFromUnknown, tutorSmsLandingPath } from '../../utils/tutorSmsLandingPath';
import { tutorBookingMatchesStudentNameSearch } from '../../utils/tutorBookingStudentName';
import {
  groupHostTutorBookingRequests,
  pendingOnlineTutorUsesSeriesEndpoint,
} from '../../utils/hostTutorBookingGrouping';
import { tutorBookingDetailsPath } from '../../utils/tutorBookingDetailsPath';

type TutorNegativeDialogMode = 'decline' | 'cancel';

const TutorBookingRequestsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const focusBookingIdFromUrl = searchParams.get('bookingId')?.trim() || null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<TutorBookingFilter>(
    focusBookingIdFromUrl ? 'all' : 'needs_tutor_action',
  );
  const [summary, setSummary] = useState<TutorBookingSummaryCountsResponse | null>(null);
  const [requests, setRequests] = useState<HostTutorBookingResponse[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [negativeDialogMode, setNegativeDialogMode] =
    useState<TutorNegativeDialogMode>('decline');
  const [conflictBookingId, setConflictBookingId] = useState<string | null>(null);
  const [proposeDialogOpen, setProposeDialogOpen] = useState(false);
  const [proposeBooking, setProposeBooking] = useState<HostTutorBookingResponse | null>(null);
  const [proposingChange, setProposingChange] = useState(false);
  const [cancelChangeDialogOpen, setCancelChangeDialogOpen] = useState(false);
  const [cancelChangeReason, setCancelChangeReason] = useState('');
  const [cancelChangeTarget, setCancelChangeTarget] = useState<HostTutorBookingResponse | null>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refundTarget, setRefundTarget] = useState<HostTutorBookingResponse | null>(null);
  const [refundingId, setRefundingId] = useState<string | null>(null);
  const [collectPaymentDialogOpen, setCollectPaymentDialogOpen] = useState(false);
  const [collectPaymentTarget, setCollectPaymentTarget] = useState<HostTutorBookingResponse | null>(null);
  const [collectingPaymentId, setCollectingPaymentId] = useState<string | null>(null);
  const [focusBookingId, setFocusBookingId] = useState<string | null>(focusBookingIdFromUrl);
  const [studentSearch, setStudentSearch] = useState('');
  const focusCardRef = useRef<HTMLDivElement | null>(null);
  const didScrollToFocusRef = useRef(false);

  const filteredRequests = useMemo(
    () =>
      requests.filter((booking) =>
        tutorBookingMatchesStudentNameSearch(
          { studentProfile: booking.studentProfile, userName: booking.userName },
          studentSearch,
        ),
      ),
    [requests, studentSearch],
  );

  const fetchSummary = useCallback(async () => {
    try {
      const data = await tutorApiClient.getTutorBookingsSummary();
      setSummary(data);
    } catch {
      // Non-blocking; tab counts stay at 0 until next successful fetch.
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await tutorApiClient.getTutorBookingRequests(page, rowsPerPage, activeFilter);
      setRequests(data.content);
      setTotalElements(extractTutorBookingsPageTotalFromLegacy(data));
      setConflictBookingId((current) =>
        current && data.content.some((item) => item.bookingId === current) ? current : null
      );
    } catch (_e) {
      setError(t('tutorBookings.fetchError', "Couldn't load lessons"));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, page, rowsPerPage, t]);

  const refreshAfterMutation = useCallback(async () => {
    await Promise.all([fetchRequests(), fetchSummary()]);
    notifyTutorBookingNeedsActionCountChanged();
  }, [fetchRequests, fetchSummary]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    setPage(0);
  }, [activeFilter]);

  useEffect(() => {
    if (!focusBookingIdFromUrl) return;
    setFocusBookingId(focusBookingIdFromUrl);
    setActiveFilter('all');
    didScrollToFocusRef.current = false;
  }, [focusBookingIdFromUrl]);

  useEffect(() => {
    if (!focusBookingIdFromUrl) return;
    if (!authService.getRoles().includes('HOST')) return;
    let cancelled = false;
    (async () => {
      let tutorLookupHttpStatus: number | null = null;
      try {
        await tutorApiClient.getTutorBookingDetails(focusBookingIdFromUrl);
        tutorLookupHttpStatus = 200;
      } catch (error) {
        tutorLookupHttpStatus = httpStatusFromUnknown(error);
      }
      if (cancelled || tutorLookupHttpStatus === 200) return;
      let hostLookupHttpStatus: number | null = null;
      try {
        await hostApiClient.getHostTutorBookingDetails(focusBookingIdFromUrl);
        hostLookupHttpStatus = 200;
      } catch (error) {
        hostLookupHttpStatus = httpStatusFromUnknown(error);
      }
      if (cancelled) return;
      const target = tutorSmsLandingPath(
        focusBookingIdFromUrl,
        tutorLookupHttpStatus,
        hostLookupHttpStatus,
      );
      if (target) {
        navigate(target, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [focusBookingIdFromUrl, navigate]);

  useEffect(() => {
    if (!focusBookingId || loading || didScrollToFocusRef.current) return;
    if (!requests.some((row) => row.bookingId === focusBookingId)) return;
    const node = focusCardRef.current;
    if (!node) return;
    didScrollToFocusRef.current = true;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusBookingId, loading, requests]);

  const handleFilterChange = (filter: TutorBookingFilter) => {
    setActiveFilter(filter);
    setStudentSearch('');
    if (filter !== 'all' && focusBookingId) {
      setFocusBookingId(null);
      if (searchParams.has('bookingId')) {
        const next = new URLSearchParams(searchParams);
        next.delete('bookingId');
        setSearchParams(next, { replace: true });
      }
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatSeriesWhen = (startTime: string, endTime: string) => {
    const start = new Date(startTime.endsWith('Z') ? startTime : `${startTime}Z`);
    const end = new Date(endTime.endsWith('Z') ? endTime : `${endTime}Z`);
    const date = start.toLocaleString(i18n.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const endLabel = end.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
    return `${date}–${endLabel}`;
  };

  const formatSeriesAmount = (amount: number, currencyCode: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode || 'PLN',
    }).format(amount);

  const handleAccept = async (bookingId: string) => {
    setProcessingId(bookingId);
    setConflictBookingId(null);
    try {
      const row = requests.find((booking) => booking.bookingId === bookingId);
      if (row && pendingOnlineTutorUsesSeriesEndpoint(row)) {
        await tutorApiClient.acceptTutorBookingSeries(row.recurringSeriesId!.trim());
      } else {
        await tutorApiClient.acceptTutorBooking(bookingId);
      }
      await refreshAfterMutation();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setConflictBookingId(bookingId);
        setError(null);
      } else {
        setError(t('tutorBookings.acceptError', "Couldn't confirm this lesson"));
      }
    } finally {
      setProcessingId(null);
    }
  };

  const openDeclineDialog = (bookingId: string, initialReason: string = '') => {
    setSelectedBookingId(bookingId);
    setDeclineReason(initialReason);
    setNegativeDialogMode('decline');
    setDeclineDialogOpen(true);
  };

  const openCancelLessonDialog = (row: HostTutorBookingResponse) => {
    if (!canCancelConfirmedTutorBooking(row.status, row.startTime)) return;
    setSelectedBookingId(row.bookingId);
    setDeclineReason('');
    setNegativeDialogMode('cancel');
    setDeclineDialogOpen(true);
  };

  const closeDeclineDialog = () => {
    if (processingId === selectedBookingId) return;
    setDeclineDialogOpen(false);
    setDeclineReason('');
    setSelectedBookingId(null);
    setNegativeDialogMode('decline');
  };

  const handleDeclineOrCancelLesson = async () => {
    if (!selectedBookingId) return;
    const reason = declineReason.trim();
    if (!reason) return;
    const isCancel = negativeDialogMode === 'cancel';
    setProcessingId(selectedBookingId);
    try {
      const row = requests.find((booking) => booking.bookingId === selectedBookingId);
      if (!isCancel && row && pendingOnlineTutorUsesSeriesEndpoint(row)) {
        await tutorApiClient.declineTutorBookingSeries(row.recurringSeriesId!.trim(), reason);
      } else {
        await tutorApiClient.declineTutorBooking(selectedBookingId, reason);
      }
      setDeclineDialogOpen(false);
      setDeclineReason('');
      setConflictBookingId((current) => (current === selectedBookingId ? null : current));
      setSelectedBookingId(null);
      setNegativeDialogMode('decline');
      await refreshAfterMutation();
      if (isCancel) {
        setActiveFilter('cancelled');
      }
    } catch {
      setError(
        isCancel
          ? t('tutorBookings.cancelLessonError', "Couldn't cancel this lesson")
          : t('tutorBookings.declineError', "Couldn't decline this lesson"),
      );
    } finally {
      setProcessingId(null);
    }
  };

  const openProposeChangeDialog = (row: HostTutorBookingResponse) => {
    setProposeBooking(row);
    setProposeDialogOpen(true);
    setConflictBookingId(null);
  };

  const handleProposeChangeSubmit = async (
    newStartTime: string,
    reason: string,
    autoAcceptTutorBooking: boolean,
  ) => {
    if (!proposeBooking) return;
    setProposingChange(true);
    setError(null);
    try {
      await tutorApiClient.proposeTutorBookingChange(proposeBooking.bookingId, {
        newStartTime,
        reason: reason || undefined,
        autoAcceptTutorBooking,
      });
      setProposeDialogOpen(false);
      setProposeBooking(null);
      await refreshAfterMutation();
    } catch {
      setError(t('hostTutorBookings.proposeChangeError', 'Failed to propose schedule change'));
    } finally {
      setProposingChange(false);
    }
  };

  const openCancelChangeDialog = (row: HostTutorBookingResponse) => {
    setCancelChangeTarget(row);
    setCancelChangeReason('');
    setCancelChangeDialogOpen(true);
  };

  const handleCancelChangeProposal = async () => {
    if (!cancelChangeTarget?.changeProposal) return;
    setProcessingId(cancelChangeTarget.bookingId);
    setError(null);
    try {
      await tutorApiClient.cancelTutorBookingChange(
        cancelChangeTarget.bookingId,
        cancelChangeTarget.changeProposal.id,
        { reason: cancelChangeReason.trim() || undefined },
      );
      setCancelChangeDialogOpen(false);
      setCancelChangeTarget(null);
      setCancelChangeReason('');
      await refreshAfterMutation();
    } catch {
      setError(t('hostTutorBookings.cancelChangeError', 'Failed to cancel change proposal'));
    } finally {
      setProcessingId(null);
    }
  };

  const showModerationActions = (row: HostTutorBookingResponse) =>
    !isPastTutorBookingStart(row.startTime) &&
    isTutorBookingModeratable(row.status) &&
    !hasPendingTutorChangeProposal(row.changeProposal);

  const openRefundDialog = (row: HostTutorBookingResponse) => {
    if (!canRefundTutorBooking(row)) return;
    setRefundTarget(row);
    setRefundReason('');
    setRefundDialogOpen(true);
  };

  const closeRefundDialog = () => {
    if (refundingId) return;
    setRefundDialogOpen(false);
    setRefundReason('');
    setRefundTarget(null);
  };

  const handleRefundSubmit = async () => {
    if (!refundTarget || !canRefundTutorBooking(refundTarget)) return;
    setRefundingId(refundTarget.bookingId);
    setError(null);
    try {
      const trimmed = refundReason.trim();
      await tutorApiClient.refundTutorBooking(
        refundTarget.bookingId,
        trimmed ? { refund_reason: trimmed } : {},
      );
      setRefundDialogOpen(false);
      setRefundReason('');
      setRefundTarget(null);
      await refreshAfterMutation();
    } catch {
      setError(t('hostTutorBookings.refundError', 'Failed to issue refund'));
    } finally {
      setRefundingId(null);
    }
  };

  const openCollectPaymentDialog = (row: HostTutorBookingResponse) => {
    if (!canCollectTutorBookingPayment(row)) return;
    setCollectPaymentTarget(row);
    setCollectPaymentDialogOpen(true);
  };

  const closeCollectPaymentDialog = () => {
    if (collectingPaymentId) return;
    setCollectPaymentDialogOpen(false);
    setCollectPaymentTarget(null);
  };

  const handleCollectPaymentSubmit = async () => {
    if (!collectPaymentTarget || !canCollectTutorBookingPayment(collectPaymentTarget)) return;
    setCollectingPaymentId(collectPaymentTarget.bookingId);
    setError(null);
    try {
      await tutorApiClient.collectTutorBookingPayment(collectPaymentTarget.bookingId);
      setCollectPaymentDialogOpen(false);
      setCollectPaymentTarget(null);
      await refreshAfterMutation();
    } catch {
      setError(t('hostTutorBookings.collectPaymentError', 'Failed to mark payment as collected'));
    } finally {
      setCollectingPaymentId(null);
    }
  };

  const pendingActionCount = summary?.needsTutorAction ?? 0;

  const emptyFilterKey =
    activeFilter === 'needs_user_action' ? 'awaiting_user' : activeFilter;

  const emptyDescriptionForFilter = t(`tutorBookingFilters.empty.${emptyFilterKey}`, {
    defaultValue: t(
      'tutorBookings.emptyDescription',
      'When students request a lesson with you, they will appear here for review.',
    ),
  });

  const renderConflictFooter = (row: HostTutorBookingResponse) => {
    if (conflictBookingId !== row.bookingId) return null;
    return (
      <Alert severity="warning" sx={{ width: '100%' }}>
        <Typography variant="body2">
          {t(
            'tutorBookings.conflictOnAccept',
            "This time is already taken, so you can't confirm the lesson.",
          )}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
          {canHostProposeTutorBookingChange(row.status, row.startTime, row.changeProposal) && (
            <Button size="small" variant="outlined" color="primary" onClick={() => openProposeChangeDialog(row)}>
              {t('tutorBookings.suggestNewTime', 'New time')}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => openDeclineDialog(row.bookingId)}
          >
            {t('tutorBookings.declineLesson', 'Decline')}
          </Button>
        </Stack>
      </Alert>
    );
  };

  return (
    <>
      <TutorBookingRequestsPageShell
        title={t('tutorBookings.title', 'Lessons')}
        subtitle={t(
          'tutorBookings.subtitle',
          'Confirm a request, decline it, or suggest another time.',
        )}
        pendingActionCount={pendingActionCount}
        loading={loading}
        hasLoadedOnce={requests.length > 0 || !loading}
        error={error}
        onRetry={() => fetchRequests()}
        onDismissError={() => setError(null)}
        emptyTitle={t(`tutorBookingFilters.empty.${emptyFilterKey}`, {
          defaultValue: t('tutorBookings.emptyTitle', 'No lessons yet'),
        })}
        emptyDescription={emptyDescriptionForFilter}
        isEmpty={requests.length === 0 && !loading}
      >
        <TutorBookingFilterTabs
          filters={TUTOR_BOOKING_REQUEST_FILTERS}
          activeFilter={activeFilter}
          summary={summary}
          onChange={handleFilterChange}
          labelOverrides={{
            needs_user_action: t('tutorBookingFilters.awaiting_user', 'Waiting for the student'),
          }}
          trailing={
            requests.length > 0 ? (
              <TutorBookingStudentSearchField
                value={studentSearch}
                onChange={setStudentSearch}
                disabled={loading}
              />
            ) : undefined
          }
        />
        {requests.length > 0 ? (
          <>
            {filteredRequests.length > 0 ? (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {groupHostTutorBookingRequests(filteredRequests).map((entry) => {
                  if (entry.kind === 'series') {
                    return (
                      <Grid item xs={12} key={entry.seriesId}>
                        <TutorSeriesRequestCard
                          seriesId={entry.seriesId}
                          bookings={entry.bookings}
                          busy={processingId === entry.seriesId}
                          formatWhen={formatSeriesWhen}
                          formatAmount={formatSeriesAmount}
                          onAcceptSeries={(seriesId) => tutorApiClient.acceptTutorBookingSeries(seriesId)}
                          onAcceptSelected={(seriesId, bookingIds, declineReason) =>
                            tutorApiClient.acceptSelectedTutorBookingSeries(seriesId, bookingIds, declineReason)
                          }
                          onAcceptOne={(bookingId) => tutorApiClient.acceptTutorBooking(bookingId)}
                          onDeclineSeries={(seriesId, reason) =>
                            tutorApiClient.declineTutorBookingSeries(seriesId, reason)
                          }
                          onOpen={(booking) => navigate(tutorBookingDetailsPath('tutor', booking.bookingId))}
                          onChanged={refreshAfterMutation}
                          onError={setError}
                        />
                      </Grid>
                    );
                  }
                  const row = entry.booking;
                  const busy = processingId === row.bookingId || proposingChange;
                  const highlighted = focusBookingId === row.bookingId;
                  return (
                    <Grid item xs={12} md={6} key={row.bookingId}>
                      <div ref={highlighted ? focusCardRef : undefined}>
                        <TutorBookingRequestCard
                          row={row}
                          perspective="tutor"
                          busy={busy}
                          processingBookingId={processingId}
                          showModeration={showModerationActions(row)}
                          canPropose={canHostProposeTutorBookingChange(row.status, row.startTime, row.changeProposal)}
                          canCancelProposal={canHostCancelTutorBookingChangeProposal(row.changeProposal)}
                          canCancelLesson={canCancelConfirmedTutorBooking(row.status, row.startTime)}
                          acceptDisabled={tutorBookingAcceptDisabled(row.status, row.startTime)}
                          declineDisabled={tutorBookingDeclineDisabled(row.status, row.startTime)}
                          onAccept={handleAccept}
                          onDecline={openDeclineDialog}
                          onPropose={openProposeChangeDialog}
                          onCancelProposal={openCancelChangeDialog}
                          onCancelLesson={openCancelLessonDialog}
                          conflictFooter={renderConflictFooter(row)}
                          canRefund={canRefundTutorBooking(row)}
                          onRefund={openRefundDialog}
                          refundLoading={refundingId === row.bookingId}
                          canCollectPayment={canCollectTutorBookingPayment(row)}
                          onCollectPayment={openCollectPaymentDialog}
                          collectPaymentLoading={collectingPaymentId === row.bookingId}
                          highlighted={highlighted}
                        />
                      </div>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                {t(
                  'tutorBookingRequests.noStudentMatches',
                  'No lessons on this page match that student.',
                )}
              </Alert>
            )}
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={isMobile ? [] : [5, 10, 25]}
              labelRowsPerPage={t('common.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('common.pagination.displayedRows', { from, to, count })
              }
              sx={{ mt: 1 }}
            />
          </>
        ) : null}
      </TutorBookingRequestsPageShell>
      <BookingRejectionDialog
        open={declineDialogOpen}
        title={
          negativeDialogMode === 'decline'
            ? t('tutorBookings.declineTitle', 'Decline this lesson request?')
            : t('tutorBookings.cancelLessonTitle', 'Cancel this lesson?')
        }
        description={
          negativeDialogMode === 'decline'
            ? t(
                'tutorBookings.declineDescription',
                'The student will be notified and will see the reason you write.',
              )
            : t(
                'tutorBookings.cancelLessonDescription',
                'This lesson is already confirmed. The student will be notified and will see the reason you write.',
              )
        }
        reason={declineReason}
        reasonLabel={
          negativeDialogMode === 'decline'
            ? t('tutorBookings.declineReason', 'Reason')
            : t('tutorBookings.cancelLessonReason', 'Reason')
        }
        reasonPlaceholder={t(
          negativeDialogMode === 'decline'
            ? 'roomBookingRequests.declineReasonPlaceholder'
            : 'tutorBookings.cancelLessonReasonPlaceholder',
          negativeDialogMode === 'decline'
            ? 'Briefly explain why you cannot accept this request'
            : 'Briefly explain why you need to cancel this lesson',
        )}
        reasonHelperText={t(
          negativeDialogMode === 'decline'
            ? 'roomBookingRequests.declineReasonHelper'
            : 'tutorBookings.cancelLessonReasonHelper',
          'A reason is required and will be shared with the student.',
        )}
        confirmLabel={
          negativeDialogMode === 'decline'
            ? t('roomBookingRequests.decline')
            : t('tutorBookings.cancelLessonButton', 'Cancel lesson')
        }
        cancelLabel={
          negativeDialogMode === 'decline'
            ? t('common.cancel', 'Cancel')
            : t('tutorBookings.keepLesson', 'Keep lesson')
        }
        closeLabel={t('common.close', 'Close')}
        loading={processingId === selectedBookingId}
        onReasonChange={setDeclineReason}
        onClose={closeDeclineDialog}
        onConfirm={handleDeclineOrCancelLesson}
        confirmColor="error"
        fullScreen={isMobile}
        notice={
          negativeDialogMode === 'decline' &&
          requests.some(
            (booking) =>
              booking.bookingId === selectedBookingId && pendingOnlineTutorUsesSeriesEndpoint(booking),
          ) ? (
            <Typography variant="body2">
              {t('bookings.declineSeriesWarning', 'This will affect all sessions in this series.')}
            </Typography>
          ) : undefined
        }
      />
      <HostTutorBookingProposeChangeDialog
        open={proposeDialogOpen}
        booking={proposeBooking}
        submitting={proposingChange}
        perspective="tutor"
        onClose={() => {
          if (!proposingChange) {
            setProposeDialogOpen(false);
            setProposeBooking(null);
          }
        }}
        onSubmit={handleProposeChangeSubmit}
      />
      <Dialog
        open={cancelChangeDialogOpen}
        onClose={() => !processingId && setCancelChangeDialogOpen(false)}
      >
        <DialogTitle>{t('bookings.changeProposal.cancel', 'Cancel proposal')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            margin="dense"
            label={t('bookings.changeProposal.cancelReason', 'Reason (optional)')}
            value={cancelChangeReason}
            onChange={(e) => setCancelChangeReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelChangeDialogOpen(false)} disabled={!!processingId}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCancelChangeProposal}
            disabled={!!processingId}
            startIcon={processingId ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('bookings.changeProposal.confirmCancel', 'Cancel proposal')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={refundDialogOpen} onClose={closeRefundDialog}>
        <DialogTitle>{t('hostTutorBookings.refundDialogTitle', 'Issue refund')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t(
              'hostTutorBookings.refundDialogDescription',
              'Refund or release payment for this cancelled tutor booking. The backend applies Stripe refund or payment-intent cancellation rules.',
            )}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            margin="dense"
            label={t('hostTutorBookings.refundReasonLabel', 'Refund note (optional)')}
            placeholder={t('hostTutorBookings.refundReasonPlaceholder', 'e.g. duplicate charge')}
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRefundDialog} disabled={!!refundingId}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRefundSubmit}
            disabled={!!refundingId}
            startIcon={refundingId ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('hostTutorBookings.issueRefund', 'Issue refund')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={collectPaymentDialogOpen} onClose={closeCollectPaymentDialog}>
        <DialogTitle>{t('hostTutorBookings.collectPaymentDialogTitle', 'Mark as paid')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'hostTutorBookings.collectPaymentDialogDescription',
              'Confirm that on-site payment was collected for this lesson. The booking will be marked paid and the collection time will be recorded.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCollectPaymentDialog} disabled={!!collectingPaymentId}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCollectPaymentSubmit}
            disabled={!!collectingPaymentId}
            startIcon={
              collectingPaymentId ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {t('hostTutorBookings.collectPayment', 'Mark as paid')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TutorBookingRequestsPage;
